import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import patientService, {
  type PatientAllergy,
  type PatientAllergyStatus,
  type PatientMedicalProfile,
} from '../../services/patientService';

export type { PatientAllergy, PatientAllergyStatus, PatientMedicalProfile };

/**
 * Three mutually exclusive display states the UI must show for patient allergies:
 *
 * - `nka-confirmed`   — Kroll has an explicit "No Known Allergies" flag set. Safe to show green/confirmed.
 * - `has-allergies`   — There are documented allergies in PatAlg to display.
 * - `not-documented`  — No NKA flag AND no allergies. This is a *warning* state: the pharmacist
 *                       has not recorded anything, so the UI must make it clear that allergy
 *                       status is unknown and should be verified with the patient.
 */
export type AllergyDisplayState = 'nka-confirmed' | 'has-allergies' | 'not-documented';

export function getAllergyDisplayState(
  status: PatientAllergyStatus | undefined,
): AllergyDisplayState | undefined {
  if (!status) return undefined;
  if (status.allergies.length > 0) return 'has-allergies';
  if (status.noKnownAllergies) return 'nka-confirmed';
  return 'not-documented';
}

/**
 * Fetches a patient's allergy status (NKA flag + documented allergies) from Kroll via MQTT.
 *
 * Intended for lightweight uses — patient info cards, counseling headers, hardcopy summaries —
 * where the full clinical interactions screen (useClinicalInteractions) is overkill.
 */
export function usePatientAllergies(
  pharmacyId: string | undefined,
  patientId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<PatientAllergyStatus>({
    queryKey: ['patient', 'allergies', pharmacyId, patientId],
    queryFn: () => patientService.getPatientAllergiesFromKroll(pharmacyId!, patientId!),
    enabled: !!pharmacyId && !!patientId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes — allergy data changes rarely
    retry: 1,
  });
}

const medicalProfileKey = (compositePatientId: string | undefined) =>
  ['patient', 'medical-profile', compositePatientId] as const;

/**
 * Fetches the PHOX-owned medical profile (allergies + medical conditions) that
 * PHOX maintains natively, independent of the read-only Kroll allergy pull.
 * `compositePatientId` is the `{pharmacyId}_{patientId}` id used everywhere in
 * Patient 360.
 */
export function usePatientMedicalProfile(
  compositePatientId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<PatientMedicalProfile>({
    queryKey: medicalProfileKey(compositePatientId),
    queryFn: () => patientService.getMedicalProfile(compositePatientId!),
    enabled: !!compositePatientId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Mutation to save the PHOX-owned medical profile. On success it refreshes the
 * medical-profile query so Patient 360 and the shared allergies card (which
 * reads the same source) reflect the new master list immediately.
 */
export function useUpdatePatientMedicalProfile(compositePatientId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: {
      allergies?: string[];
      medicalConditions?: string[];
      medications?: string[];
      weight?: string | null;
    }) => patientService.updateMedicalProfile(compositePatientId!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: medicalProfileKey(compositePatientId) });
    },
  });
}
